import { nanoid } from 'nanoid'
import Cookies from 'js-cookie'
import { getTimestamp, sendGTMEvent, isDataLayerAvailable } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { getParserToken } from 'entities/Auth/lib/setToken'
import { requestCloseAgentPanel } from 'entities/WebMcp/lib/agentPanelSignal'

const id = nanoid()

const getAllCookies = () => {
  return document.cookie
    .split('; ')
    .map((item) => item.split('='))
    .reduce((c: { [key: string]: string }, n) => {
      const key = n[0]
      const value = n[1]

      if (key) {
        c[key] = value
      }

      return c
    }, {})
}

const payload = (entities: IEntitiesStore) => {
  const user = entities.usersStore.user
  const number = entities.numbersStore.organizationNumber
  const config = entities.configStore.config
  const plan = entities.subscriptionStore.plan
  const z_index = ZIndex.BASE_30
  const anonymous_id = (() => {
    const exist_id = localStorage.getItem('intercom-anonymous_id')

    if (!exist_id) {
      localStorage.setItem('intercom-anonymous_id', id)
    }

    return localStorage.getItem('intercom-anonymous_id')
  })()

  if (!user || !number || !config || !plan || !entities.organizationStore.id) {
    return {
      anonymous_id: anonymous_id,
      z_index: z_index,
    }
  }

  const company = {
    id: entities.organizationStore.id,
    name: entities.organizationStore.name,
    createdAt: entities.organizationStore.created_at,
    ownerFirstName: user.first_name,
    ownerLastName: user.last_name,
    ownerName: user.full_name,
    phone: number?.formatted_number,
    plan: plan.id,
    planStatus: entities.subscriptionStore.status,
  }

  return {
    email: user.email,
    id: String(user.id),
    user_id: String(user.id),
    created_at: getTimestamp(user.createdAt),
    name: user.name,
    phone: number.formatted_number,
    avatar: user.avatarInfo.image,
    user_hash: config.intercom,
    company,
    z_index: z_index,
  }
}

type IinitIntercomProps = {
  fromAuthorizedApp?: boolean
  entities: IEntitiesStore
}

export const initIntercom = async ({ fromAuthorizedApp, entities }: IinitIntercomProps) => {
  if (!isDataLayerAvailable()) return

  if (fromAuthorizedApp) {
    await Promise.all([
      entities.organizationStore.initPromise,
      entities.configStore.initPromise,
      entities.numbersStore.initFetchOrganizationNumberPromise,
      entities.subscriptionStore.initPromise,
    ])
  }

  const data = getParserToken()

  if (!data || !data?.hipaa) {
    sendGTMEvent({
      eventName: 'intercom_init',
      eventParams: { intercom_payload: payload(entities) },
    })
    watchIntercomOpen()
  }
}

// https://developers.intercom.com/installing-intercom/web/methods
export const updateIntercom = (entities: IEntitiesStore) => {
  if (!window.Intercom) return

  const data = getParserToken()

  if (!data || !data?.hipaa) {
    window.Intercom('update', payload(entities))
  }
}

/** Whether the Intercom messenger can be invoked now (booted, and not a HIPAA session). */
export const isIntercomAvailable = (): boolean => {
  if (!window.Intercom) return false
  const data = getParserToken()
  return !data || !data?.hipaa
}

// The agent panel sits at 9999 over this corner, so the messenger opens behind it.
const showMessenger = (...command: unknown[]) => {
  if (!isIntercomAvailable()) return

  window.Intercom?.(...command)
  requestCloseAgentPanel()
}

export const openIntercom = () => showMessenger('show')

export const openIntercomMessages = () => showMessenger('showMessages')

export const openIntercomNewMessage = (prePopulatedMessage?: string) =>
  showMessenger('showNewMessage', prePopulatedMessage ?? '')

/** Open a help center article inside the messenger. Expects the numeric article id. */
export const openIntercomArticle = (articleId: string) =>
  showMessenger('showArticle', /^\d+$/.test(articleId) ? Number(articleId) : articleId)

export const closeIntercom = () => {
  if (!isIntercomAvailable()) return

  window.Intercom?.('hide')
}

// GTM boots the messenger and gives us no callback for it.
const BOOT_POLL_INTERVAL_MS = 500
const BOOT_POLL_ATTEMPTS = 60

let watchingIntercom = false
// Bumped on session reset so a poll started before it knows it is stale.
let watchGeneration = 0

const waitForIntercom = (generation: number): Promise<boolean> =>
  new Promise((resolve) => {
    if (window.Intercom) return resolve(true)

    let attempts = 0
    const timer = setInterval(() => {
      attempts += 1
      const stale = generation !== watchGeneration

      if (window.Intercom || stale || attempts >= BOOT_POLL_ATTEMPTS) {
        clearInterval(timer)
        resolve(!stale && Boolean(window.Intercom))
      }
    }, BOOT_POLL_INTERVAL_MS)
  })

// Catches the routes this module cannot see, such as Intercom's own launcher.
export const watchIntercomOpen = async (): Promise<void> => {
  if (watchingIntercom) return
  watchingIntercom = true

  const generation = watchGeneration
  const booted = await waitForIntercom(generation)

  // A newer watcher owns the flag now, so leave it alone.
  if (generation !== watchGeneration) return

  if (!booted || !isIntercomAvailable()) {
    watchingIntercom = false
    return
  }

  window.Intercom?.('onShow', () => requestCloseAgentPanel())
}

export const clearSessionIntercom = () => {
  // Shutdown drops the onShow subscription.
  watchingIntercom = false
  watchGeneration += 1

  if (!window.Intercom) {
    for (const key in getAllCookies()) {
      if (/^intercom-session-.+/.test(key)) {
        Cookies.remove(key, { domain: '.salesmessage.com' })
      }
    }

    return
  }

  window.Intercom('shutdown')
}

export const setHideIntercom = (value: boolean) => {
  if (!window.Intercom) return

  const data = getParserToken()

  if (!data || !data?.hipaa) {
    window.Intercom('update', {
      hide_default_launcher: value,
    })
  }
}
